import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import axiosInstance from "../config/axios";
import { useRole } from "../context/RoleContext";

function statusTone(status) {
  const value = String(status || "").toLowerCase();
  if (value === "complete" || value === "signed" || value === "filled" || value === "high") {
    return "bg-emerald-100 text-emerald-800";
  }
  if (value === "incomplete" || value === "blank" || value === "missing" || value === "low" || value === "partial") {
    return "bg-amber-100 text-amber-800";
  }
  return "bg-rose-100 text-rose-800";
}

function statusLabel(status) {
  const value = String(status || "").replace(/_/g, " ").trim();
  if (!value) return "Unknown";
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function Badge({ status }) {
  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${statusTone(
        status,
      )}`}
    >
      {statusLabel(status)}
    </span>
  );
}

function FieldList({ title, items, empty }) {
  if (!items?.length) {
    return (
      <div>
        <h4 className="text-sm font-semibold text-gray-800 mb-2">{title}</h4>
        <p className="text-sm text-gray-500">{empty}</p>
      </div>
    );
  }
  return (
    <div>
      <h4 className="text-sm font-semibold text-gray-800 mb-2">{title}</h4>
      <ul className="space-y-1.5">
        {items.map((item, idx) => (
          <li key={`${title}-${idx}`} className="text-sm text-gray-800">
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
}

const ApsReviewPanel = ({ tradeId, listingId }) => {
  const { isAdmin, isSuperUser, isBroker } = useRole();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [payload, setPayload] = useState(null);
  const recordId = listingId || tradeId;

  const load = async (force) => {
    if (!recordId) return;
    if (force) setRefreshing(true);
    else setLoading(true);
    setError("");
    try {
      const url = listingId
        ? `/listing-documents/${listingId}/document-review`
        : `/trade-documents/${tradeId}/aps-review`;
      const config = { timeout: 180000 };
      const res = force
        ? await axiosInstance.post(url, {}, config)
        : await axiosInstance.get(url, config);
      const data = res.data || {};
      const emailResult = data.emailResult;
      // Only toast when this request actually sent mail (not a prior send).
      if (
        emailResult?.sent &&
        !emailResult?.skipped &&
        Array.isArray(emailResult.recipients) &&
        emailResult.recipients.length
      ) {
        toast.success(
          `Compliance review was sent to ${emailResult.recipients.join(", ")}.`,
          { autoClose: 10000 },
        );
      }
      setPayload(data);
    } catch (err) {
      setPayload(null);
      setError(
        err.response?.data?.message ||
          "Could not load the compliance review.",
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tradeId, listingId]);

  if (!isAdmin && !isSuperUser && !isBroker) return null;

  const report = payload?.report || {};
  const property = report.property || {};
  const parties = report.parties || {};
  const signatures = Array.isArray(report.signatures) ? report.signatures : [];
  const fields = Array.isArray(report.fields) ? report.fields : [];
  const missing = Array.isArray(report.missingItems)
    ? report.missingItems.filter(Boolean)
    : [];
  const issues = Array.isArray(report.issues)
    ? report.issues.filter(Boolean)
    : [];
  const generated = payload?.generatedAt
    ? new Date(payload.generatedAt).toLocaleString()
    : "";

  return (
    <div className="mb-6 rounded-xl border border-blue-100 bg-white shadow-sm overflow-hidden">
      <div className="flex flex-wrap items-start justify-between gap-3 px-5 py-4 bg-blue-50 border-b border-blue-100">
        <div>
          <h3 className="text-lg font-semibold text-blue-900">
            Compliance Review
          </h3>
          <p className="text-sm text-gray-600">
            Automated review of uploaded documents. An administrator will
            manually review and follow up if changes are needed.
            {payload?.documentCount
              ? ` ${payload.documentCount} file${
                  payload.documentCount === 1 ? "" : "s"
                } on file.`
              : payload?.fileName
                ? ` Files: ${payload.fileName}`
                : ""}
          </p>
        </div>
        <button
          type="button"
          onClick={() => load(true)}
          disabled={loading || refreshing}
          className="px-3 py-1.5 text-sm rounded-md bg-blue-900 text-white hover:bg-blue-800 disabled:bg-gray-400"
        >
          {refreshing ? "Reviewing..." : "Refresh report"}
        </button>
      </div>

      <div className="px-5 py-4">
        {loading ? (
          <p className="text-sm text-gray-600">
            Claude is reading the uploaded documents. This can take up to a
            couple of minutes. The agent is emailed automatically when the
            review finishes.
          </p>
        ) : error ? (
          <p className="text-sm text-amber-800">{error}</p>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <Badge status={report.overallStatus} />
              {payload?.emailedToAgent ? (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                  Sent to agent
                </span>
              ) : payload?.report ? (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800">
                  Email pending
                </span>
              ) : null}
              {report.completeness ? (
                <span className="text-sm text-gray-600">
                  Completeness: {statusLabel(report.completeness)}
                </span>
              ) : null}
              {payload?.incremental ? (
                <span className="text-xs font-medium text-blue-800 bg-blue-50 px-2 py-0.5 rounded-full">
                  New uploads batch
                </span>
              ) : null}
              {generated ? (
                <span className="text-xs text-gray-500">
                  Generated {generated}
                </span>
              ) : null}
            </div>
            {payload?.emailedToAgent &&
            (payload.agentEmailedTo || []).filter(Boolean).length ? (
              <p className="text-xs text-blue-900 mb-3">
                Review was sent to{" "}
                {(payload.agentEmailedTo || []).filter(Boolean).join(", ")}
                {payload.agentEmailedAt
                  ? ` · ${new Date(payload.agentEmailedAt).toLocaleString()}`
                  : ""}
                .
              </p>
            ) : payload?.report && !payload?.emailedToAgent ? (
              <p className="text-xs text-amber-800 mb-3">
                Email to the agent will send automatically.
              </p>
            ) : null}
            {Array.isArray(payload?.reviewedBatchFileNames) &&
            payload.reviewedBatchFileNames.length &&
            payload.incremental ? (
              <div className="mb-4 rounded-md border border-blue-100 bg-blue-50/60 px-3 py-2">
                <h4 className="text-sm font-semibold text-blue-900 mb-1">
                  Documents in this automated review
                </h4>
                <ul className="list-disc pl-5 text-sm text-blue-900 space-y-0.5">
                  {payload.reviewedBatchFileNames.map((name, idx) => (
                    <li key={`batch-${idx}`}>{name}</li>
                  ))}
                </ul>
              </div>
            ) : null}
            {report.formType ? (
              <p className="text-sm font-medium text-gray-800 mb-2">
                {report.formType}
              </p>
            ) : null}
            {Array.isArray(payload?.fileNames) && payload.fileNames.length ? (
              <div className="mb-4">
                <h4 className="text-sm font-semibold text-gray-800 mb-1">
                  {listingId ? "Files on this listing" : "Files on this trade"}
                </h4>
                <ul className="list-disc pl-5 text-sm text-gray-700 space-y-0.5">
                  {payload.fileNames.map((name, idx) => (
                    <li key={`file-${idx}`}>{name}</li>
                  ))}
                </ul>
              </div>
            ) : null}
            {report.summary ? (
              <p className="text-sm text-gray-800 leading-relaxed mb-4">
                {report.summary}
              </p>
            ) : null}

            {Array.isArray(report.documents) && report.documents.length ? (
              <div className="mb-4">
                <h4 className="text-sm font-semibold text-gray-800 mb-2">
                  Documents uploaded
                </h4>
                <div className="space-y-2">
                  {report.documents.map((doc, idx) => (
                    <div
                      key={`doc-${idx}`}
                      className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-3"
                    >
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <p className="text-sm font-medium text-gray-900">
                          {doc.documentType || doc.fileName || "Document"}
                        </p>
                        <Badge status={doc.status} />
                        {doc.signatures && doc.signatures !== "n/a" ? (
                          <span className="text-xs text-gray-500">
                            Signatures: {statusLabel(doc.signatures)}
                          </span>
                        ) : null}
                      </div>
                      {doc.fileName && doc.fileName !== doc.documentType ? (
                        <p className="text-xs text-gray-500 mb-1">
                          {doc.fileName}
                        </p>
                      ) : null}
                      {doc.summary ? (
                        <p className="text-sm text-gray-700 leading-relaxed">
                          {doc.summary}
                        </p>
                      ) : null}
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div className="rounded-lg bg-gray-50 p-3">
                <h4 className="text-sm font-semibold text-gray-800 mb-2">
                  Property &amp; deal
                </h4>
                <dl className="text-sm space-y-1 text-gray-700">
                  <div>
                    <span className="text-gray-500">Address: </span>
                    {property.address || "—"}
                    {property.unit ? `, Unit ${property.unit}` : ""}
                  </div>
                  <div>
                    <span className="text-gray-500">Price: </span>
                    {property.purchasePrice || property.listedPrice || "—"}
                  </div>
                  <div>
                    <span className="text-gray-500">Deposit: </span>
                    {property.deposit || "—"}
                  </div>
                  <div>
                    <span className="text-gray-500">Completion: </span>
                    {property.completionDate || "—"}
                  </div>
                  <div>
                    <span className="text-gray-500">Irrevocable: </span>
                    {property.irrevocableDate || "—"}
                  </div>
                </dl>
              </div>
              <div className="rounded-lg bg-gray-50 p-3">
                <h4 className="text-sm font-semibold text-gray-800 mb-2">
                  Parties
                </h4>
                <p className="text-sm text-gray-700">
                  <span className="text-gray-500">Buyers: </span>
                  {(parties.buyers || []).filter(Boolean).join(", ") || "—"}
                </p>
                <p className="text-sm text-gray-700 mt-1">
                  <span className="text-gray-500">Sellers: </span>
                  {(parties.sellers || []).filter(Boolean).join(", ") || "—"}
                </p>
                {report.initials ? (
                  <p className="text-sm text-gray-700 mt-2">
                    <span className="text-gray-500">Initials: </span>
                    <Badge status={report.initials.status} />
                    {report.initials.notes ? (
                      <span className="ml-2">{report.initials.notes}</span>
                    ) : null}
                  </p>
                ) : null}
              </div>
            </div>

            <div className="mb-4">
              <h4 className="text-sm font-semibold text-gray-800 mb-2">
                Signatures
              </h4>
              {signatures.length === 0 ? (
                <p className="text-sm text-gray-500">
                  No signature blocks were reported.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="text-left text-gray-500">
                        <th className="py-1 pr-4 font-medium">Role</th>
                        <th className="py-1 pr-4 font-medium">Name</th>
                        <th className="py-1 pr-4 font-medium">Status</th>
                        <th className="py-1 font-medium">Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {signatures.map((row, idx) => (
                        <tr key={`sig-${idx}`} className="border-t border-gray-100">
                          <td className="py-1.5 pr-4">{row.role || "—"}</td>
                          <td className="py-1.5 pr-4">{row.name || "—"}</td>
                          <td className="py-1.5 pr-4">
                            <Badge status={row.status} />
                          </td>
                          <td className="py-1.5 text-gray-600">
                            {row.notes || ""}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <FieldList
                title="Missing or blank"
                items={missing}
                empty="No missing items were flagged."
              />
              <FieldList
                title="Issues"
                items={issues}
                empty="No extra issues were flagged."
              />
            </div>

            {fields.length ? (
              <div className="mb-4">
                <h4 className="text-sm font-semibold text-gray-800 mb-2">
                  Field checklist
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {fields.map((field, idx) => (
                    <div
                      key={`field-${idx}`}
                      className="flex items-start justify-between gap-2 rounded-md border border-gray-100 px-3 py-2"
                    >
                      <div>
                        <p className="text-sm text-gray-900">
                          {field.label || "Field"}
                        </p>
                        {field.value ? (
                          <p className="text-xs text-gray-600">{field.value}</p>
                        ) : null}
                      </div>
                      <Badge status={field.status} />
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            {report.recommendation ? (
              <div className="rounded-lg bg-blue-50 px-3 py-3">
                <h4 className="text-sm font-semibold text-blue-900 mb-1">
                  Recommendation
                </h4>
                <p className="text-sm text-gray-800 leading-relaxed">
                  {report.recommendation}
                </p>
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
};

export default ApsReviewPanel;
